/**
 * The checkout catalogue: the single source of truth for what is sold, and at
 * what price.
 *
 * Every price here is the exact integer-cent amount Shopify charges for the
 * mapped variant — there is no separate "marketing price" — less only the
 * first-kit discount stated beside it, which Recharge takes off. A plan is
 * sold as a product per billing period, and every child after the first as
 * the sibling product for that period, which Shopify prices lower, so a
 * sibling's price is one of these amounts too. A plan can be sold as other
 * products to a visitor whose promo is active, one set per code, which is
 * how the promo's discount reaches Shopify: no code goes in the cart.
 * Everything else shown in the UI (effective per-kit rates, "due today",
 * savings percentages, order totals) is derived from these numbers by
 * `pricing.ts`, and `catalogue.test.ts` enforces the arithmetic properties
 * the derivations rely on. If a number on screen is wrong, it is wrong here.
 */

import type { PromoCode } from '../promo/codes'

export type PlanId = 'kit-studio' | 'online-only'
export type BillingPeriod = 'annual' | 'monthly'

export interface Price {
  /** Integer cents — the exact amount charged, before any first-kit discount. Never a float. */
  priceCents: number
  /**
   * What the first kit costs less, in integer cents, on a kit plan whose
   * Recharge plan discounts it: every year's first kit on an annual plan, so
   * every charge is less by it, but only the very first kit on a monthly one,
   * by the plan's dynamic pricing. Absent when no kit is discounted.
   */
  firstKitDiscountCents?: number
}

/** A price the checkout can charge: the Shopify variant it is charged as. */
export interface PlanPrice extends Price {
  /** The Shopify variant (numeric id) whose selling plan the checkout puts in the cart. */
  variantId: string
}

/** A plan as a card shows it: what it is, and what it costs. */
export interface Plan {
  id: string
  name: string
  description: string
  /** What step 2 calls one child's subscription: a kit for the physical box, a plan otherwise. */
  noun: 'kit' | 'plan'
  mostPopular?: boolean
  /** Card artwork: a square thumbnail on the components CDN. */
  imageUrl: string
  monthly: Price
  annual: Price
}

/** A plan the checkout can put in a cart, as it is sold to a visitor without a promo. */
export interface PurchasablePlan extends Plan {
  id: PlanId
  monthly: PlanPrice
  annual: PlanPrice
  /**
   * What every child after the first is charged instead: the plan's sibling
   * products in Shopify, one per billing period like the plan's own.
   */
  sibling: Record<BillingPeriod, PlanPrice>
  /**
   * The products the plan is sold as instead to a visitor whose promo is
   * active, by its code. A plan without them sells the same to everyone.
   */
  promos?: Record<PromoCode, Products>
}

/** What a plan is sold as: a product per billing period, and a sibling product for each. */
export type Products = Pick<PurchasablePlan, 'monthly' | 'annual' | 'sibling'>

/** The plan thumbnails: square JPEGs served from the CDN alongside the bundle. */
const THUMBNAILS = 'https://homepage-components.codeverse.com/static-assets/images/plan-thumbnails'

function thumbnail(name: string): string {
  return `${THUMBNAILS}/${name}.jpg`
}

export const plans: readonly PurchasablePlan[] = [
  {
    id: 'kit-studio',
    name: 'Codeverse Kit + Studio',
    description: 'The full hands-on experience, shipped monthly.',
    noun: 'kit',
    mostPopular: true,
    imageUrl: thumbnail('kit'),
    // Without a promo, no kit is discounted.
    // platform and shopify-emails need these and FIRSTFREE's variant ids before a
    // release sells them: see the README's open items.
    monthly: { priceCents: 3495, variantId: '52118871343350' },
    annual: { priceCents: 31140, variantId: '52118941630710' },
    sibling: {
      monthly: { priceCents: 3145, variantId: '52118900867318' },
      annual: { priceCents: 28020, variantId: '52118962700534' },
    },
    promos: {
      // The first kit 40% off: once on a monthly plan, and every year on an annual one.
      WELCOME40: {
        monthly: { priceCents: 3495, firstKitDiscountCents: 1398, variantId: '51102004838646' },
        annual: { priceCents: 31140, firstKitDiscountCents: 1038, variantId: '51775876399350' },
        sibling: {
          monthly: { priceCents: 3145, firstKitDiscountCents: 1258, variantId: '51775168807158' },
          annual: { priceCents: 28020, firstKitDiscountCents: 934, variantId: '51775878267126' },
        },
      },
      // The first kit free: once on a monthly plan, and every year on an annual one.
      FIRSTFREE: {
        monthly: { priceCents: 3495, firstKitDiscountCents: 3495, variantId: '52118373433590' },
        annual: { priceCents: 31140, firstKitDiscountCents: 2595, variantId: '52118369108214' },
        sibling: {
          monthly: { priceCents: 3145, firstKitDiscountCents: 3145, variantId: '52118372122870' },
          annual: { priceCents: 28020, firstKitDiscountCents: 2335, variantId: '52118140190966' },
        },
      },
    },
  },
  {
    id: 'online-only',
    name: 'Online Only',
    description: 'Start simple. Build apps & games digitally, anytime.',
    noun: 'plan',
    imageUrl: thumbnail('digital'),
    monthly: { priceCents: 2495, variantId: '51102132142326' },
    annual: { priceCents: 23940, variantId: '51775867584758' },
    sibling: {
      monthly: { priceCents: 2245, variantId: '51775273795830' },
      annual: { priceCents: 21540, variantId: '51775873515766' },
    },
  },
]

/** Every set of products a plan is sold as: without a promo, then with each. */
export function productsOf(plan: PurchasablePlan): Products[] {
  return [plan, ...Object.values(plan.promos ?? {})]
}

/** The variants a set of products charges as: the plan's and the sibling's, in both periods. */
export function variantsOf({ monthly, annual, sibling }: Products): string[] {
  return [monthly, annual, sibling.monthly, sibling.annual].map((price) => price.variantId)
}

/** Every variant the checkout sells, once each, with a promo and without. */
export const variantIds: readonly string[] = [
  ...new Set(plans.flatMap(productsOf).flatMap(variantsOf)),
]

/**
 * Concierge is arranged through the contact page rather than bought here: it
 * is listed beside the plans at its prices, and its card leads to that page
 * instead of into checkout, so it has no Shopify variants.
 */
export const concierge: Plan = {
  id: 'concierge',
  name: 'Concierge',
  description: 'Premium coaching and personalized support.',
  noun: 'plan',
  imageUrl: thumbnail('concierge'),
  monthly: { priceCents: 17900 },
  annual: { priceCents: 178800 },
}

export const DEFAULT_PLAN_ID: PlanId = 'kit-studio'

/** The plan as it is sold to a visitor with `promo` active: its products for that code, if any. */
function withPromo(plan: PurchasablePlan, promo: PromoCode | null): PurchasablePlan {
  const products = promo ? plan.promos?.[promo] : undefined
  return products ? { ...plan, ...products } : plan
}

/** Every plan, as it is sold to a visitor with `promo` active, or none. */
export function plansFor(promo: PromoCode | null): PurchasablePlan[] {
  return plans.map((plan) => withPromo(plan, promo))
}

/**
 * The plan as it is sold to a visitor with `promo` active, or none. Accepts
 * any string so storage hydration can validate untrusted input.
 */
export function planById(id: string, promo: PromoCode | null = null): PurchasablePlan | undefined {
  const plan = plans.find((plan) => plan.id === id)
  return plan && withPromo(plan, promo)
}

/**
 * What the child at `index` (0-based) is charged, and the variant it is
 * charged as: the first child pays the plan's price, every sibling the
 * sibling product's. The checkout shows prices and builds the cart from this
 * one rule, so the total on screen is the total Shopify charges.
 */
export function childPrice(plan: PurchasablePlan, period: BillingPeriod, index: number): PlanPrice {
  return index === 0 ? plan[period] : plan.sibling[period]
}

export const MAX_CHILDREN = 4

// The live Shopify store. The checkout page's embed sets no `data-shop-domain`,
// so this is the shop it sells from. The attribute names another shop per
// mount, for staging and tests: it is how the e2e fixture points the handoff
// at an interceptable host.
export const DEFAULT_SHOP_DOMAIN = 'shop.codeverse.com'
